import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/api';

const Playoffs = () => {
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [bracket, setBracket] = useState(null);
  const [picture, setPicture] = useState(null);
  const [picViewMode, setPicViewMode] = useState('list');
  const [eliminateMode, setEliminateMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSeasons();
  }, []);

  useEffect(() => {
    if (selectedSeason) {
      fetchBracket();
    }
  }, [selectedSeason]);

  const fetchSeasons = async () => {
    try {
      const response = await api.get('/seasons');
      setSeasons(response.data);
      // Auto-select active season or most recent
      const activeSeason = response.data.find(s => s.isActive);
      if (activeSeason) {
        setSelectedSeason(activeSeason.id);
      } else if (response.data.length > 0) {
        setSelectedSeason(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  };

  const fetchBracket = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/playoffs/bracket/${selectedSeason}`);
      const bracketData = response.data;
      setBracket(bracketData);

      // Only fetch the picture when there is no real bracket yet
      const hasBracketData = bracketData && (
        bracketData['Play-In'] || bracketData['First Round'] || bracketData.Finals ||
        (bracketData.format === 'custom' && bracketData.rounds &&
          Object.values(bracketData.rounds).some(r => r.length > 0))
      );
      if (!hasBracketData) {
        try {
          const picResponse = await api.get(`/playoffs/picture/${selectedSeason}`);
          setPicture(picResponse.data);
        } catch {
          setPicture(null);
        }
      } else {
        setPicture(null);
      }
    } catch (error) {
      console.error('Error fetching bracket:', error);
      setBracket(null);
      setPicture(null);
    } finally {
      setLoading(false);
    }
  };

  const renderMatchup = (series) => {
    if (!series) {
      return (
        <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded border-2 border-dashed border-gray-300 dark:border-gray-600 text-center text-gray-400 text-sm">
          TBD
        </div>
      );
    }

    const higherWon = series.winnerId === series.higherSeedId;
    const lowerWon = series.winnerId === series.lowerSeedId;

    // Sort and number games for display
    const playedGames = (series.games || [])
      .filter(g => g.played)
      .sort((a, b) => new Date(a.gameDate) - new Date(b.gameDate) || a.id - b.id);

    return (
      <div className={`bg-white dark:bg-gray-800 p-3 rounded shadow-md dark:shadow-gray-900 border-2 ${series.isComplete ? 'border-green-400 dark:border-green-700' : 'border-blue-400'}`}>
        {/* Higher Seed */}
        <div className={`flex justify-between items-center p-2 rounded ${higherWon ? 'bg-green-100 dark:bg-green-900/30 font-bold' : ''} ${lowerWon ? 'opacity-60' : ''}`}>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">{series.higherSeedRank ?? series.seriesNumber}</span>
            <span className="text-sm">{series.higherSeed.city} {series.higherSeed.name}</span>
          </div>
          <span className="font-bold text-lg">{series.higherSeedWins || 0}</span>
        </div>

        {/* Lower Seed */}
        <div className={`flex justify-between items-center p-2 rounded mt-1 ${lowerWon ? 'bg-green-100 dark:bg-green-900/30 font-bold' : ''} ${higherWon ? 'opacity-60' : ''}`}>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">{series.lowerSeedRank ?? (series.seriesNumber + 8)}</span>
            <span className="text-sm">{series.lowerSeed.city} {series.lowerSeed.name}</span>
          </div>
          <span className="font-bold text-lg">{series.lowerSeedWins || 0}</span>
        </div>

        {/* Game scores */}
        {playedGames.length > 0 && (
          <div className="mt-2 border-t border-gray-100 dark:border-gray-700 pt-2 space-y-0.5">
            {playedGames.map((game, i) => {
              const home = game.homeTeam?.abbreviation || '?';
              const away = game.awayTeam?.abbreviation || '?';
              const hs = game.homeScore ?? 0;
              const as = game.awayScore ?? 0;
              return (
                <div key={game.id} className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 px-1">
                  <span className="font-medium text-gray-400">G{i + 1}</span>
                  <span className="font-mono">{home} <span className="font-bold text-gray-700 dark:text-gray-200">{hs}</span> – <span className="font-bold text-gray-700 dark:text-gray-200">{as}</span> {away}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Series Info + detail link */}
        <div className="mt-2 text-xs text-center">
          {series.isComplete && series.winner ? (
            <span className="text-green-600 dark:text-green-400 font-semibold">
              ✓ {series.winner.city} {series.winner.name}
            </span>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">{series.seriesFormat || 'Best-of-7'}</span>
          )}
        </div>
        {playedGames.length > 0 && (
          <div className="mt-2 text-center">
            <Link
              to={`/playoffs/series/${series.id}`}
              className="text-xs text-blue-500 dark:text-blue-400 hover:underline font-medium"
            >
              View details & stats →
            </Link>
          </div>
        )}
      </div>
    );
  };

  const renderConferenceBracket = (conferenceData, conferenceName) => {
    if (!conferenceData) return null;

    const playIn = conferenceData.filter(s => s.round === 'Play-In');
    const firstRound = conferenceData.filter(s => s.round === 'First Round').sort((a, b) => a.seriesNumber - b.seriesNumber);
    const semiFinals = conferenceData.filter(s => s.round === 'Conference Semifinals').sort((a, b) => a.seriesNumber - b.seriesNumber);
    const confFinals = conferenceData.filter(s => s.round === 'Conference Finals');

    const SLOT_H = 168; // height of each first-round slot — 168px handles two-line team names + winner text
    const COL_W = 200;
    const CONN_W = 36;
    const HEADER_H = 32;

    const r1Count = Math.max(firstRound.length, 4);
    const totalBracketH = r1Count * SLOT_H;

    const rounds = [
      { matchups: firstRound,  name: 'First Round',      count: r1Count },
      { matchups: semiFinals,  name: 'Conf. Semifinals', count: Math.max(semiFinals.length, r1Count / 2) },
      { matchups: confFinals,  name: 'Conf. Finals',     count: 1 },
    ];

    // For TBD slots: label with which series could advance here
    const getFeederLabels = (roundIdx, slotIdx) => {
      if (roundIdx === 0) return null;
      const prev = rounds[roundIdx - 1].matchups;
      const f1 = prev[slotIdx * 2];
      const f2 = prev[slotIdx * 2 + 1];
      return { f1, f2 };
    };

    const teamAbbr = (team) => team?.abbreviation || team?.name || '?';

    const renderSlot = (series, roundIdx, slotIdx) => {
      if (series) return renderMatchup(series);
      const feeders = getFeederLabels(roundIdx, slotIdx);
      if (!feeders) return renderMatchup(null);
      const { f1, f2 } = feeders;
      const label1 = f1 ? `W: ${teamAbbr(f1.higherSeed)} / ${teamAbbr(f1.lowerSeed)}` : 'TBD';
      const label2 = f2 ? `W: ${teamAbbr(f2.higherSeed)} / ${teamAbbr(f2.lowerSeed)}` : 'TBD';
      return (
        <div className="bg-gray-50 dark:bg-gray-800 rounded border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden">
          <div className="px-3 py-2 text-xs text-gray-400 italic truncate">🏀 {label1}</div>
          <div className="border-t border-dashed border-gray-200 dark:border-gray-700" />
          <div className="px-3 py-2 text-xs text-gray-400 italic truncate">🏀 {label2}</div>
        </div>
      );
    };

    // SVG connector strip between two adjacent round columns
    const renderConnector = (fromRoundIdx, numPairs) => {
      const slotH = SLOT_H * Math.pow(2, fromRoundIdx);
      const lines = [];
      for (let p = 0; p < numPairs; p++) {
        const topY = p * 2 * slotH + slotH / 2;
        const botY = (p * 2 + 1) * slotH + slotH / 2;
        const midY = (topY + botY) / 2;
        lines.push(
          <line key={`t${p}`} x1="0"         y1={topY} x2={CONN_W / 2} y2={topY} />,
          <line key={`b${p}`} x1="0"         y1={botY} x2={CONN_W / 2} y2={botY} />,
          <line key={`v${p}`} x1={CONN_W / 2} y1={topY} x2={CONN_W / 2} y2={botY} />,
          <line key={`m${p}`} x1={CONN_W / 2} y1={midY} x2={CONN_W}     y2={midY} />
        );
      }
      return (
        <svg key={`conn-${fromRoundIdx}`} width={CONN_W} height={totalBracketH}
          style={{ flexShrink: 0, marginTop: HEADER_H }}>
          <g stroke="#94a3b8" strokeWidth="1.5" fill="none">{lines}</g>
        </svg>
      );
    };

    const elements = [];
    rounds.forEach((round, rIdx) => {
      const slotH = SLOT_H * Math.pow(2, rIdx);
      const slots = Array.from({ length: round.count }, (_, i) => round.matchups[i] || null);
      elements.push(
        <div key={`round-${rIdx}`} style={{ width: COL_W, flexShrink: 0 }}>
          <div className="text-xs font-bold text-center text-gray-400 uppercase tracking-wide"
            style={{ height: HEADER_H, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 4 }}>
            {round.name}
          </div>
          {slots.map((series, slotIdx) => (
            <div key={slotIdx} style={{ height: slotH, display: 'flex', alignItems: 'center', padding: '0 4px' }}>
              <div style={{ width: '100%' }}>{renderSlot(series, rIdx, slotIdx)}</div>
            </div>
          ))}
        </div>
      );
      if (rIdx < rounds.length - 1) {
        elements.push(renderConnector(rIdx, Math.ceil(round.count / 2)));
      }
    });

    // Conference champion column
    const champ = confFinals.length > 0 && confFinals[0].isComplete && confFinals[0].winner
      ? confFinals[0].winner : null;
    elements.push(
      <div key="champ" style={{ width: COL_W / 2, flexShrink: 0 }}>
        <div className="text-xs font-bold text-center text-gray-400 uppercase tracking-wide"
          style={{ height: HEADER_H, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 4 }}>
          To Finals
        </div>
        <div style={{ height: totalBracketH, display: 'flex', alignItems: 'center', padding: '0 4px' }}>
          {champ ? (
            <div className="bg-gradient-to-r from-blue-100 to-red-100 dark:from-blue-900/40 dark:to-red-900/40 p-3 rounded shadow text-center w-full">
              <div className="font-bold text-sm">{champ.city}</div>
              <div className="font-bold text-sm">{champ.name}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Conf. Champion</div>
            </div>
          ) : (
            <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded border-2 border-dashed border-gray-300 dark:border-gray-600 text-center text-gray-400 text-xs w-full">
              TBD
            </div>
          )}
        </div>
      </div>
    );

    return (
      <div className="mb-8">
        <h3 className="text-2xl font-bold mb-4 text-center">{conferenceName} Conference</h3>

        {/* Play-In games (shown above the main bracket tree) */}
        {playIn.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Play-In</h4>
            <div className="grid grid-cols-2 gap-3">
              {playIn.sort((a, b) => a.seriesNumber - b.seriesNumber).map(series => (
                <div key={series.id}>{renderMatchup(series)}</div>
              ))}
            </div>
          </div>
        )}

        <div className="overflow-x-auto pb-2">
          <div className="flex items-start" style={{ minWidth: 'max-content' }}>
            {elements}
          </div>
        </div>
      </div>
    );
  };

  const renderFinals = () => {
    if (!bracket || !bracket.Finals || bracket.Finals.length === 0) {
      return (
        <div className="text-center py-8">
          <h3 className="text-2xl font-bold mb-4">NBA Finals</h3>
          <div className="bg-gray-100 dark:bg-gray-700 p-6 rounded-lg inline-block border-2 border-dashed border-gray-300 dark:border-gray-600">
            <p className="text-gray-400">Waiting for Conference Champions</p>
          </div>
        </div>
      );
    }

    const finals = bracket.Finals[0];
    return (
      <div className="text-center py-8">
        <h3 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-red-600">
          NBA Finals
        </h3>
        <div className="max-w-md mx-auto">
          {renderMatchup(finals)}
          {finals.winner && (
            <div className="mt-6 p-6 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg shadow-xl">
              <div className="text-white">
                <div className="text-2xl font-bold mb-2">🏆 CHAMPIONS 🏆</div>
                <div className="text-3xl font-bold">{finals.winner.city}</div>
                <div className="text-3xl font-bold">{finals.winner.name}</div>
              </div>
            </div>
          )}
          <div className="mt-4">
            <Link
              to="/finals"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-bold px-6 py-3 rounded-xl shadow-md hover:shadow-lg transition text-sm"
            >
              🏆 Finals-Seite ansehen / View Finals Page
            </Link>
          </div>
        </div>
      </div>
    );
  };

  // ── Playoff Picture helpers (shown when no bracket exists yet) ──────────
  const renderPictureTeamRow = (seed, team, placeholder) => {
    const isEliminated = eliminateMode && team && team.gamesRemaining > 0;
    return (
      <div className={`flex items-center gap-2 px-3 py-2 ${isEliminated ? 'opacity-50' : ''}`}>
        <span className="text-xs font-bold text-gray-400 w-5 text-right flex-shrink-0">{seed}</span>
        {team ? (
          <>
            <img
              src={`/images/teams/${team.name.toLowerCase().replace(/\s/g, '')}.svg`}
              alt=""
              className="w-6 h-6 object-contain flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className={`flex-1 text-sm font-semibold truncate ${isEliminated ? 'line-through text-gray-400' : ''}`}>
              {team.city} {team.name}
            </span>
            <span className={`text-xs font-mono flex-shrink-0 ${isEliminated ? 'text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>
              <span className={isEliminated ? 'text-gray-400' : 'text-green-600 dark:text-green-400 font-bold'}>{team.wins}</span>
              -
              <span className={isEliminated ? 'text-gray-400' : 'text-red-500 font-bold'}>{team.losses}</span>
            </span>
            {!isEliminated && team.gamesRemaining > 0 && (
              <span
                className="ml-0.5 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 px-1 py-0.5 rounded font-medium flex-shrink-0"
                title={`Needs ${team.gamesRemaining} more game(s) to officially qualify`}
              >
                ⚠️-{team.gamesRemaining}
              </span>
            )}
            {isEliminated && (
              <span className="ml-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-500 px-1 py-0.5 rounded font-medium flex-shrink-0">
                ❌ elim.
              </span>
            )}
          </>
        ) : (
          <span className="flex-1 text-sm italic text-orange-400">{placeholder || 'TBD'}</span>
        )}
      </div>
    );
  };

  const renderPictureMatchup = (matchup, idx) => {
    const team1Elim = eliminateMode && matchup.team1?.gamesRemaining > 0;
    const team2Elim = eliminateMode && matchup.team2?.gamesRemaining > 0;
    const hasAutoAdvance = team1Elim || team2Elim;
    return (
      <div key={idx} className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden ${
        hasAutoAdvance ? 'border-2 border-green-300' : 'border border-gray-200 dark:border-gray-700'
      }`}>
        {hasAutoAdvance && (
          <div className="bg-green-50 px-3 py-1 text-xs font-bold text-green-700 dark:text-green-400 border-b border-green-100">
            🏆 Auto-Advance
          </div>
        )}
        {renderPictureTeamRow(matchup.seed1, matchup.team1, 'TBD')}
        <div className="border-t border-gray-100 dark:border-gray-700" />
        {renderPictureTeamRow(matchup.seed2, matchup.team2, matchup.playInPending ? 'Play-In TBD' : 'TBD')}
      </div>
    );
  };

  const renderPicturePlayInGame = (game, gameNum) => (
    <div key={gameNum} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-orange-200 overflow-hidden">
      <div className="bg-orange-50 px-3 py-1.5 text-xs font-bold text-orange-700 dark:text-orange-400 border-b border-orange-100">
        Game {gameNum}: #{game.seed1} vs #{game.seed2}
      </div>
      {renderPictureTeamRow(game.seed1, game.team1, 'TBD')}
      <div className="border-t border-gray-100 dark:border-gray-700" />
      {renderPictureTeamRow(game.seed2, game.team2, 'TBD')}
      {game.note && (
        <div className="px-3 py-1.5 text-xs text-orange-600 bg-orange-50 border-t border-orange-100">→ {game.note}</div>
      )}
    </div>
  );

  const renderPlayoffPicture = () => {
    const { season, firstRoundMatchups, playInMatchups, notQualifiedTeams } = picture;
    const isConferenceBased = firstRoundMatchups && !Array.isArray(firstRoundMatchups);

    // ── Bracket tree helpers ────────────────────────────────────────────────
    // Standard visual reorder: adjacent pairs connect in the next round.
    // e.g. top8 [1v8,2v7,3v6,4v5] → reordered [1v8,4v5,2v7,3v6]
    const computeOrder = (n) => {
      if (n <= 1) return [0];
      const top = computeOrder(n / 2);
      const bottom = top.map(i => n - 1 - i);
      const result = [];
      for (let i = 0; i < n / 2; i++) { result.push(top[i]); result.push(bottom[i]); }
      return result;
    };

    const buildRounds = (matchups) => {
      const n = matchups.length;
      if (!n) return [];
      const isPow2 = (n & (n - 1)) === 0;
      const ordered = isPow2 ? computeOrder(n).map(i => matchups[i]) : [...matchups];
      const rounds = [ordered];
      let prev = ordered;
      while (prev.length > 1) {
        const next = [];
        for (let i = 0; i + 1 < prev.length; i += 2) {
          const a = prev[i], b = prev[i + 1];
          const lbl = (m) => m.isProjected ? m.matchLabel : `#${m.seed1} vs #${m.seed2}`;
          next.push({ isProjected: true, label1: `W of ${lbl(a)}`, label2: `W of ${lbl(b)}`, matchLabel: `${lbl(a)} / ${lbl(b)}` });
        }
        if (!next.length) break;
        rounds.push(next);
        prev = next;
      }
      return rounds;
    };

    const SLOT_H = 80; // px — R1 slot height; doubles each round for alignment
    const ROUND_NAMES = { 1: ['Finals'], 2: ['1st Round', 'Finals'], 3: ['1st Round', 'Semifinals', 'Finals'], 4: ['1st Round', 'Quarterfinals', 'Semifinals', 'Finals'] };

    const renderTreeColumn = (matchups, label, titleColor) => {
      if (!matchups || !matchups.length) return null;
      const rounds = buildRounds(matchups);
      const names = ROUND_NAMES[rounds.length] || rounds.map((_, i) => `Round ${i + 1}`);
      return (
        <div className="mb-8">
          {label && <h3 className={`text-lg font-bold mb-3 ${titleColor || 'text-gray-800 dark:text-gray-200'}`}>{label}</h3>}
          <div className="overflow-x-auto pb-2">
            <div className="flex gap-4 items-start" style={{ minWidth: 'max-content' }}>
              {rounds.map((round, rIdx) => {
                const slotH = SLOT_H * Math.pow(2, rIdx);
                return (
                  <div key={rIdx} style={{ width: '210px' }}>
                    <div className="text-xs font-bold text-center text-gray-400 uppercase tracking-wide mb-2">
                      {names[rIdx] || `Round ${rIdx + 1}`}
                    </div>
                    {round.map((m, mIdx) => (
                      <div key={mIdx} style={{ height: `${slotH}px`, display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: '100%' }}>
                          {m.isProjected ? (
                            <div className="bg-gray-50 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden mx-1">
                              <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 italic">🏀 {m.label1}</div>
                              <div className="border-t border-dashed border-gray-200 dark:border-gray-700" />
                              <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 italic">🏀 {m.label2}</div>
                            </div>
                          ) : (
                            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden mx-1 ${
                              m.team1?.gamesRemaining > 0 || m.team2?.gamesRemaining > 0
                                ? 'border border-orange-200' : 'border border-gray-200 dark:border-gray-700'
                            }`}>
                              {renderPictureTeamRow(m.seed1, m.team1, 'TBD')}
                              <div className="border-t border-gray-100 dark:border-gray-700" />
                              {renderPictureTeamRow(m.seed2, m.team2, m.playInPending ? 'Play-In TBD' : 'TBD')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    };
    // ── end tree helpers ────────────────────────────────────────────────────

    const MatchupsBlock = ({ matchups, title, titleColor }) => (
      <div className="mb-6">
        {title && <h3 className={`text-lg font-bold mb-3 ${titleColor || 'text-gray-800 dark:text-gray-200'}`}>{title}</h3>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {matchups.map((m, i) => renderPictureMatchup(m, i))}
        </div>
      </div>
    );

    const PlayInBlock = ({ games, title, game3Note }) => (
      <div className="mb-6">
        <h3 className="text-base font-bold text-orange-700 dark:text-orange-400 mb-3">{title}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
          {games.map((g, i) => renderPicturePlayInGame(g, i + 1))}
        </div>
        {game3Note && <p className="text-xs text-gray-500 dark:text-gray-400 italic">Game 3 (if needed): {game3Note}</p>}
      </div>
    );

    return (
      <div>
        {/* Disclaimer */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4 text-sm text-blue-800">
          <strong>📊 Projected Playoff Picture — {season.name}</strong><br />
          Based on current standings as of {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
          Seedings update as more games are played.
          {season.minGamesForPlayoffs > 0 && ` Teams need ${season.minGamesForPlayoffs} games to officially qualify.`}
        </div>

        {/* View toggle + eliminate toggle */}
        <div className="flex flex-wrap gap-2 mb-5">
          <button
            onClick={() => setPicViewMode('list')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              picViewMode === 'list' ? 'bg-blue-600 text-white shadow' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:bg-gray-800'
            }`}
          >
            📋 List View
          </button>
          <button
            onClick={() => setPicViewMode('tree')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              picViewMode === 'tree' ? 'bg-blue-600 text-white shadow' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:bg-gray-800'
            }`}
          >
            🌳 Bracket View
          </button>
          {picture.season.minGamesForPlayoffs > 0 && (
            <button
              onClick={() => setEliminateMode(m => !m)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                eliminateMode
                  ? 'bg-red-600 text-white shadow'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:bg-gray-800'
              }`}
              title="Teams that haven't played the minimum number of games are treated as eliminated"
            >
              {eliminateMode ? '❌ Eliminated teams ON' : '✔️ Show eliminated teams'}
            </button>
          )}
        </div>

        {picViewMode === 'list' ? (
          isConferenceBased ? (
            <>
              <MatchupsBlock matchups={firstRoundMatchups.eastern} title="🔵 Eastern Conference — First Round" titleColor="text-blue-700 dark:text-blue-400" />
              {playInMatchups?.eastern && (
                <PlayInBlock games={playInMatchups.eastern} title="🔶 Eastern Conference Play-In" game3Note={playInMatchups.game3Note} />
              )}
              <MatchupsBlock matchups={firstRoundMatchups.western} title="🔴 Western Conference — First Round" titleColor="text-red-700 dark:text-red-400" />
              {playInMatchups?.western && (
                <PlayInBlock games={playInMatchups.western} title="🔶 Western Conference Play-In" game3Note={playInMatchups.game3Note} />
              )}
            </>
          ) : (
            <>
              <MatchupsBlock matchups={firstRoundMatchups} title="🏆 First Round Matchups" />
              {playInMatchups?.overall && (
                <PlayInBlock games={playInMatchups.overall} title="🔶 Play-In Tournament" game3Note={playInMatchups.game3Note} />
              )}
            </>
          )
        ) : (
          isConferenceBased ? (
            <>
              {renderTreeColumn(firstRoundMatchups.eastern, '🔵 Eastern Conference Bracket', 'text-blue-700 dark:text-blue-400')}
              {playInMatchups?.eastern && (
                <PlayInBlock games={playInMatchups.eastern} title="🔶 Eastern Conference Play-In" game3Note={playInMatchups.game3Note} />
              )}
              {renderTreeColumn(firstRoundMatchups.western, '🔴 Western Conference Bracket', 'text-red-700 dark:text-red-400')}
              {playInMatchups?.western && (
                <PlayInBlock games={playInMatchups.western} title="🔶 Western Conference Play-In" game3Note={playInMatchups.game3Note} />
              )}
            </>
          ) : (
            <>
              {renderTreeColumn(firstRoundMatchups, '🏆 Projected Bracket')}
              {playInMatchups?.overall && (
                <PlayInBlock games={playInMatchups.overall} title="🔶 Play-In Tournament" game3Note={playInMatchups.game3Note} />
              )}
            </>
          )
        )}

        {/* Teams not yet at minimum games — shown as info, still appear in bracket above */}
        {notQualifiedTeams && notQualifiedTeams.length > 0 && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="font-bold text-amber-800 mb-2">⚠️ Not yet at minimum games requirement</h3>
            <p className="text-sm text-amber-700 mb-3">
              These teams are included in the projection above but haven't reached the required {season.minGamesForPlayoffs} games yet (marked ⚠️ in bracket). Their seeding may change.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {notQualifiedTeams.map(team => (
                <div key={team.id} className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200">
                  <img
                    src={`/images/teams/${team.name.toLowerCase().replace(/\s/g, '')}.svg`}
                    alt=""
                    className="w-8 h-8 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div>
                    <div className="font-semibold text-sm">{team.city} {team.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{team.wins}W-{team.losses}L · {team.gamesPlayed} played</div>
                    <div className="text-xs text-orange-600 font-medium mt-0.5">
                      Still needs {team.gamesRemaining} more game{team.gamesRemaining !== 1 ? 's' : ''} to punch their ticket! 🎫
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
  // ─────────────────────────────────────────────────────────────────────────

  if (loading && !bracket) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-red-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-20">
            <div className="text-2xl text-gray-600 dark:text-gray-400">Loading playoffs...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-red-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-red-600">
            🏀 Playoff Bracket
          </h1>
          
          {/* Season Selector */}
          <div className="flex justify-center gap-4 items-center mb-4">
            <label className="font-semibold">Season:</label>
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="px-4 py-2 border rounded-lg bg-white dark:bg-gray-800"
            >
              {seasons.map(season => (
                <option key={season.id} value={season.id}>
                  {season.name} ({season.year})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Bracket Display */}
        {(() => {
          const isCustom = bracket && bracket.format === 'custom';
          const hasStandardData = bracket && (bracket['Play-In'] || bracket['First Round'] || bracket.Finals);
          const hasCustomData = isCustom && bracket.rounds && Object.values(bracket.rounds).some(r => r.length > 0);

          if (!bracket || (!hasStandardData && !hasCustomData)) {
            // Show projected picture once 20+ regular season games have been played
            if (picture && picture.totalGamesPlayed >= 20) {
              return <div className="px-2 pb-8">{renderPlayoffPicture()}</div>;
            }
            return (
              <div className="text-center py-20">
                <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md dark:shadow-gray-900 inline-block">
                  {picture && picture.totalGamesPlayed > 0 ? (
                    <>
                      <p className="text-xl text-gray-600 dark:text-gray-400">🏀 Season just getting started!</p>
                      <p className="text-gray-500 dark:text-gray-400 mt-2">
                        {20 - picture.totalGamesPlayed} more game{20 - picture.totalGamesPlayed !== 1 ? 's' : ''} need to be played before the playoff picture becomes available.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xl text-gray-600 dark:text-gray-400">No playoff bracket available for this season yet.</p>
                      <p className="text-gray-500 dark:text-gray-400 mt-2">Check back when playoffs begin!</p>
                    </>
                  )}
                </div>
              </div>
            );
          }

          if (isCustom) {
            // Build bracket tree for custom format
            const SLOT_H = 168;
            const COL_W = 200;
            const CONN_W = 36;
            const HEADER_H = 32;

            const customRounds = bracket.config.rounds.map(rc => ({
              name: rc.name,
              format: rc.seriesFormat,
              matchups: (bracket.rounds[rc.name] || []).sort((a, b) => a.seriesNumber - b.seriesNumber),
            }));

            // Determine first-round count from data or default to first round's matchup count
            const r1Count = Math.max(customRounds[0]?.matchups.length || 0, 2);
            const totalBracketH = r1Count * SLOT_H;

            const customTeamAbbr = (team) => team?.abbreviation || team?.name || '?';

            const renderCustomSlot = (series, roundIdx, slotIdx) => {
              if (series) return renderMatchup(series);
              if (roundIdx === 0) return renderMatchup(null);
              const prevMatchups = customRounds[roundIdx - 1]?.matchups || [];
              const f1 = prevMatchups[slotIdx * 2];
              const f2 = prevMatchups[slotIdx * 2 + 1];
              if (!f1 && !f2) return renderMatchup(null);
              const label1 = f1 ? `W: ${customTeamAbbr(f1.higherSeed)} / ${customTeamAbbr(f1.lowerSeed)}` : 'TBD';
              const label2 = f2 ? `W: ${customTeamAbbr(f2.higherSeed)} / ${customTeamAbbr(f2.lowerSeed)}` : 'TBD';
              return (
                <div className="bg-gray-50 dark:bg-gray-800 rounded border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden">
                  <div className="px-3 py-2 text-xs text-gray-400 italic truncate">🏀 {label1}</div>
                  <div className="border-t border-dashed border-gray-200 dark:border-gray-700" />
                  <div className="px-3 py-2 text-xs text-gray-400 italic truncate">🏀 {label2}</div>
                </div>
              );
            };

            const renderCustomConnector = (fromRoundIdx, numPairs) => {
              const slotH = SLOT_H * Math.pow(2, fromRoundIdx);
              const lines = [];
              for (let p = 0; p < numPairs; p++) {
                const topY = p * 2 * slotH + slotH / 2;
                const botY = (p * 2 + 1) * slotH + slotH / 2;
                const midY = (topY + botY) / 2;
                lines.push(
                  <line key={`t${p}`} x1="0"          y1={topY} x2={CONN_W / 2} y2={topY} />,
                  <line key={`b${p}`} x1="0"          y1={botY} x2={CONN_W / 2} y2={botY} />,
                  <line key={`v${p}`} x1={CONN_W / 2}  y1={topY} x2={CONN_W / 2} y2={botY} />,
                  <line key={`m${p}`} x1={CONN_W / 2}  y1={midY} x2={CONN_W}     y2={midY} />
                );
              }
              return (
                <svg key={`conn-${fromRoundIdx}`} width={CONN_W} height={totalBracketH}
                  style={{ flexShrink: 0, marginTop: HEADER_H }}>
                  <g stroke="#94a3b8" strokeWidth="1.5" fill="none">{lines}</g>
                </svg>
              );
            };

            const customElements = [];
            customRounds.forEach((round, rIdx) => {
              const slotH = SLOT_H * Math.pow(2, rIdx);
              const expectedCount = Math.max(round.matchups.length, Math.ceil(r1Count / Math.pow(2, rIdx)));
              const slots = Array.from({ length: expectedCount }, (_, i) => round.matchups[i] || null);
              customElements.push(
                <div key={`round-${rIdx}`} style={{ width: COL_W, flexShrink: 0 }}>
                  <div className="text-xs font-bold text-center text-gray-400 uppercase tracking-wide"
                    style={{ height: HEADER_H, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 4 }}>
                    {round.name}
                    {round.format && <span className="ml-1 normal-case font-normal text-gray-300">({round.format})</span>}
                  </div>
                  {slots.map((series, slotIdx) => (
                    <div key={slotIdx} style={{ height: slotH, display: 'flex', alignItems: 'center', padding: '0 4px' }}>
                      <div style={{ width: '100%' }}>{renderCustomSlot(series, rIdx, slotIdx)}</div>
                    </div>
                  ))}
                </div>
              );
              if (rIdx < customRounds.length - 1) {
                customElements.push(renderCustomConnector(rIdx, Math.ceil(expectedCount / 2)));
              }
            });

            return (
              <>
                {/* Custom Playoff Bracket */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900 p-6">
                  <div className="overflow-x-auto pb-2">
                    <div className="flex items-start" style={{ minWidth: 'max-content' }}>
                      {customElements}
                    </div>
                  </div>
                </div>

                {/* Champion Display */}
                {(() => {
                  const lastRound = bracket.config.rounds[bracket.config.rounds.length - 1];
                  const finalSeries = bracket.rounds[lastRound.name] || [];
                  const champion = finalSeries.length === 1 && finalSeries[0].isComplete && finalSeries[0].winner;
                  if (!champion) return null;
                  return (
                    <div className="text-center mt-8">
                      <div className="p-6 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg shadow-xl inline-block">
                        <div className="text-white">
                          <div className="text-2xl font-bold mb-2">🏆 CHAMPIONS 🏆</div>
                          <div className="text-3xl font-bold">{champion.city}</div>
                          <div className="text-3xl font-bold">{champion.name}</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            );
          }

          // Standard bracket display
          const hasEastern = !!(bracket['Play-In']?.Eastern?.length || bracket['First Round']?.Eastern?.length || bracket['Conference Semifinals']?.Eastern?.length);
          const hasWestern = !!(bracket['Play-In']?.Western?.length || bracket['First Round']?.Western?.length || bracket['Conference Semifinals']?.Western?.length);
          return (
            <>
              {/* Eastern Conference */}
              {hasEastern && renderConferenceBracket(
                [...(bracket['Play-In']?.Eastern || []), ...(bracket['First Round']?.Eastern || []), ...(bracket['Conference Semifinals']?.Eastern || []), ...(bracket['Conference Finals']?.Eastern || [])],
                'Eastern'
              )}

              {/* Western Conference */}
              {hasWestern && renderConferenceBracket(
                [...(bracket['Play-In']?.Western || []), ...(bracket['First Round']?.Western || []), ...(bracket['Conference Semifinals']?.Western || []), ...(bracket['Conference Finals']?.Western || [])],
                'Western'
              )}

              {/* NBA Finals */}
              {renderFinals()}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default Playoffs;
